import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import StatCard, { STAT_CHIP } from '../../components/ui/StatCard';
import GradientIcon from '../../components/ui/GradientIcon';
import { Building2, Users, PlayCircle, BarChart3 } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { dashboardService, sessionService } from '../../services';

/**
 * Super Admin Dashboard
 * Modern platform-wide overview for super administrators
 */

const SuperAdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platformStats, setPlatformStats] = useState({
    totalOrganizations: 0,
    totalUsers: 0,
    activeSessions: 0,
    totalSessions: 0,
  });
  const [growthData, setGrowthData] = useState({
    newOrgsThisMonth: 0,
    newUsersThisWeek: 0,
    sessionGrowthPercent: 0,
    sessionsThisMonth: 0,
    hasSessionComparison: false,
  });
  const [recentOrganizations, setRecentOrganizations] = useState([]);
  const [subscriptionBreakdown, setSubscriptionBreakdown] = useState([]);
  const [completedSessions, setCompletedSessions] = useState([]);
  const [platformActivity, setPlatformActivity] = useState([]);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch dashboard stats
      const statsResponse = await dashboardService.getStats();
      const stats = statsResponse.data;

      setPlatformStats({
        totalOrganizations: stats.overview.totalOrganizations,
        totalUsers: stats.overview.totalUsers,
        activeSessions: stats.sessions.byStatus.live || 0,
        totalSessions: stats.overview.totalSessions || 0,
      });

      // Set growth data from real API
      setGrowthData({
        newOrgsThisMonth: stats.growth?.newOrgsThisMonth || 0,
        newUsersThisWeek: stats.growth?.newUsersThisWeek || 0,
        sessionGrowthPercent: stats.growth?.sessionGrowthPercent || 0,
        sessionsThisMonth: stats.growth?.sessionsThisMonth || 0,
        hasSessionComparison: stats.growth?.hasSessionComparison || false,
      });

      // Set subscription breakdown — bucketed by EFFECTIVE state. Backend
      // (dashboardController) routes any expired/past_due orgs into byPlan.expired
      // via isPlanExpired(), so the donut shows real composition instead of
      // counting lapsed orgs in their stale nominal plan slice.
      //
      // Solo trainers (type='individual' orgs) are bucketed separately on
      // the backend so they don't pollute the client-org tier counts. Surfaced
      // here as their own row + their expired count is folded into the global
      // Expired row so admins see one combined "lapsed accounts" number.
      const totalOrgs = stats.overview.totalOrganizations || 1;
      const byPlan = stats.organizations.byPlan || {};
      const indivBuckets = stats.organizations.individualTrainers?.byPlan || {};

      const indivActiveCount =
        (indivBuckets.free || 0)
        + (indivBuckets.pro || 0)
        + (indivBuckets.pro_plus || 0)
        + (indivBuckets.enterprise_starter || 0)
        + (indivBuckets.enterprise_scale || 0);
      const indivExpiredCount = indivBuckets.expired || 0;
      const expiredCombined = (byPlan.expired || 0) + indivExpiredCount;

      // Combined denominator — clients + solo trainers — so percentages
      // across the rows sum to 100% and the donut renders one consistent pie.
      const combinedTotal = totalOrgs + indivActiveCount + indivExpiredCount;
      const denom = combinedTotal || 1;

      setSubscriptionBreakdown([
        {
          tier: 'Free',
          name: 'Free',
          count: byPlan.free || 0,
          percentage: Math.round(((byPlan.free || 0) / denom) * 100),
          chartColor: '#94A3B8',
          text: 'text-neutral-600',
        },
        {
          tier: 'Pro',
          name: 'Pro',
          count: byPlan.pro || 0,
          percentage: Math.round(((byPlan.pro || 0) / denom) * 100),
          chartColor: '#3B82F6',
          text: 'text-blue-600',
        },
        {
          tier: 'Pro Plus',
          name: 'Pro Plus',
          count: byPlan.pro_plus || 0,
          percentage: Math.round(((byPlan.pro_plus || 0) / denom) * 100),
          chartColor: '#06B6D4',
          text: 'text-cyan-600',
        },
        {
          tier: 'Enterprise Starter',
          name: 'Enterprise Starter',
          count: byPlan.enterprise_starter || 0,
          percentage: Math.round(((byPlan.enterprise_starter || 0) / denom) * 100),
          chartColor: '#F59E0B',
          text: 'text-amber-600',
        },
        {
          tier: 'Enterprise Scale',
          name: 'Enterprise Scale',
          count: byPlan.enterprise_scale || 0,
          percentage: Math.round(((byPlan.enterprise_scale || 0) / denom) * 100),
          chartColor: '#22C55E',
          text: 'text-green-600',
        },
        {
          tier: 'Individual Trainer',
          name: 'Individual Trainer',
          count: indivActiveCount,
          percentage: Math.round((indivActiveCount / denom) * 100),
          chartColor: '#A855F7',
          text: 'text-violet-600',
        },
        {
          tier: 'Expired',
          name: 'Expired',
          count: expiredCombined,
          percentage: Math.round((expiredCombined / denom) * 100),
          chartColor: '#EF4444',
          text: 'text-red-600',
        },
      ]);

      // Fetch recent activity
      const activityResponse = await dashboardService.getRecentActivity(10);
      const activityData = activityResponse.data;

      // Set recent organizations (limit to last 5)
      setRecentOrganizations(
        activityData.recentOrganizations.slice(0, 5).map(org => ({
          id: org._id,
          name: org.name,
          tier: org.plan,
          users: 0, // Would need to be fetched separately or included in API
          joined: getTimeAgo(org.createdAt),
        }))
      );

      // Set platform activity
      setPlatformActivity(
        activityData.activities.slice(0, 6).map(activity => ({
          type: activity.type,
          title: getActivityAction(activity),
          details: getActivityDetails(activity),
          time: getTimeAgo(activity.date),
          category: activity.type === 'organization' ? 'Organizations' : activity.type === 'user' ? 'Users' : 'Sessions',
          color: activity.type === 'organization' ? 'bg-purple-100 text-purple-600' : activity.type === 'user' ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600',
        }))
      );

      // Fetch completed sessions
      const sessionsResponse = await sessionService.getSessions({ status: 'completed', limit: 5 });
      setCompletedSessions(sessionsResponse.data || []);

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays < 7) return `${diffDays} days ago`;
    return `${Math.floor(diffDays / 7)} weeks ago`;
  };

  const getActivityAction = (activity) => {
    switch (activity.type) {
      case 'organization': return 'New organization registered';
      case 'user': return 'New user joined';
      case 'session': return 'Session created';
      default: return 'Activity';
    }
  };

  const getActivityDetails = (activity) => {
    switch (activity.type) {
      case 'organization': return `${activity.data.name} signed up for ${activity.data.plan} plan`;
      case 'user': return `${activity.data.firstName} ${activity.data.lastName} joined ${activity.data.organization?.name || 'platform'}`;
      case 'session': return `${activity.data.name} scheduled`;
      default: return '';
    }
  };

  // Helper to format growth text
  const formatGrowthText = (value, period) => {
    if (value === 0) return `No new ${period}`;
    return `+${value} ${period}`;
  };

  // Stats cards with real data
  const statsCards = [
    { title: 'Total Organizations', value: platformStats.totalOrganizations,                icon: Building2,  variant: 'blue' },
    { title: 'Total Users',         value: platformStats.totalUsers.toLocaleString(),        icon: Users,      variant: 'violet' },
    { title: 'Active Sessions',     value: platformStats.activeSessions,                     icon: PlayCircle, variant: 'emerald' },
    { title: 'Total Sessions',      value: platformStats.totalSessions,                      icon: BarChart3,  variant: 'amber' },
  ];

  // Activity color mapping
  const getActivityColor = (type) => {
    switch (type) {
      case 'organization': return 'text-blue-600 bg-blue-50';
      case 'session': return 'text-green-600 bg-green-50';
      case 'payment': return 'text-amber-600 bg-amber-50';
      case 'user': return 'text-purple-600 bg-purple-50';
      default: return 'text-neutral-600 bg-neutral-50';
    }
  };

  return (
    <DashboardLayout title="Super Admin Dashboard">
      <div className="p-4 lg:p-6 space-y-5 bg-gradient-to-br from-neutral-50 to-white min-h-screen">
        {/* Welcome Header */}
        <div className="mb-4">
          <h1 className="text-xl sm:text-2xl font-bold text-neutral-900 mb-1">Platform Overview</h1>
          <p className="text-sm text-neutral-600">Monitor and manage the entire training platform</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statsCards.map((stat) => (
            <StatCard key={stat.title} label={stat.title} value={stat.value} icon={stat.icon} variant={stat.variant} />
          ))}
        </div>

        {/* Recent Organizations - Full Width */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-base font-bold text-neutral-900">Recent Organizations</h2>
                  <p className="text-xs text-neutral-500">Newly registered organizations</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => navigate('/admin/organizations')}>
                  View All
                </Button>
              </div>

              <div className="space-y-2">
                {recentOrganizations.map((org) => (
                  <div
                    key={org.id}
                    className="group flex items-center justify-between p-3 bg-gradient-to-br from-neutral-50 to-white rounded-lg hover:shadow-md transition-all duration-300 cursor-pointer border border-neutral-100 hover:border-primary-200"
                    onClick={() => navigate(`/admin/organizations/${org.id}`)}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-700 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow group-hover:scale-105 transition-transform">
                        {org.name.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-semibold text-neutral-900 group-hover:text-primary-600 transition-colors">{org.name}</h4>
                          <Badge
                            variant={org.tier === 'pro' ? 'primary' : 'default'}
                            size="sm"
                          >
                            {org.tier}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-neutral-600">
                          <span className="flex items-center gap-1">
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                              />
                            </svg>
                            {org.users} users
                          </span>
                          <span className="text-neutral-300">•</span>
                          <span>{org.joined}</span>
                        </div>
                      </div>
                    </div>
                    <svg className="w-4 h-4 text-neutral-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                ))}
              </div>

              <div className="mt-4 pt-4 border-t border-neutral-200">
                <Button variant="primary" size="sm" fullWidth onClick={() => navigate('/admin/organizations')}>
                  View All Organizations
                </Button>
              </div>
        </div>

        {/* Subscription Breakdown - Full Width */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-base font-bold text-neutral-900">Subscriptions</h2>
              <p className="text-xs text-neutral-500">Tier distribution across organizations</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/billing')}>
              Manage
            </Button>
          </div>

          <div className="flex flex-col lg:flex-row items-center gap-8">
            {/* Donut Chart */}
            <div className="relative flex-shrink-0" style={{ width: 200, height: 200 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={subscriptionBreakdown.some(t => t.count > 0)
                      ? subscriptionBreakdown
                      : [{ name: 'No Data', count: 1, chartColor: '#E5E7EB' }]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={88}
                    dataKey="count"
                    nameKey="name"
                    paddingAngle={3}
                    strokeWidth={0}
                  >
                    {(subscriptionBreakdown.some(t => t.count > 0)
                      ? subscriptionBreakdown
                      : [{ name: 'No Data', count: 1, chartColor: '#E5E7EB' }]
                    ).map((tier) => (
                      <Cell key={tier.name} fill={tier.chartColor} />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) => {
                      if (active && payload?.length && payload[0].name !== 'No Data') {
                        return (
                          <div className="bg-white rounded-lg shadow-lg border border-neutral-100 px-3 py-2">
                            <p className="text-xs font-semibold text-neutral-900">{payload[0].name}</p>
                            <p className="text-xs text-neutral-600">{payload[0].value} organizations</p>
                          </div>
                        );
                      }
                      return null;
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                {/* Sum the breakdown rows so the donut center matches the
                    rendered pie — clients + solo trainers, including expired. */}
                <span className="text-2xl sm:text-3xl font-bold text-neutral-900">
                  {subscriptionBreakdown.reduce((sum, t) => sum + (t.count || 0), 0)}
                </span>
                <span className="text-xs text-neutral-500">Total</span>
              </div>
            </div>

            {/* Tier Breakdown List */}
            <div className="flex-1 w-full space-y-2">
              {subscriptionBreakdown.map((tier) => (
                <div
                  key={tier.tier}
                  className="flex items-center gap-3 p-3 rounded-xl hover:bg-neutral-50 transition-colors cursor-pointer group"
                  onClick={() => navigate('/admin/billing')}
                >
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: tier.chartColor }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-sm font-medium text-neutral-700 group-hover:text-neutral-900 transition-colors">{tier.tier}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-neutral-900">{tier.count}</span>
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded-md ${tier.text} bg-opacity-10`}
                          style={{ backgroundColor: `${tier.chartColor}18` }}
                        >
                          {tier.percentage}%
                        </span>
                      </div>
                    </div>
                    <div className="w-full bg-neutral-100 rounded-full h-1.5 overflow-hidden">
                      <div
                        className="h-1.5 rounded-full transition-all duration-700 ease-out"
                        style={{ width: `${Math.max(tier.percentage, 2)}%`, backgroundColor: tier.chartColor }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Completed Sessions Across Platform */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-bold text-neutral-900">Completed Sessions</h2>
              <p className="text-xs text-neutral-500">Recently completed sessions across all organizations</p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/sessions')}>
              View All
            </Button>
          </div>

          <div className="overflow-x-auto -mx-4 px-4">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="border-b border-neutral-200">
                  <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Session Name</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Organization</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Trainer</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Participants</th>
                  <th className="text-left py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Status</th>
                  <th className="text-right py-2 px-3 text-xs font-semibold text-neutral-600 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {completedSessions.map((session) => (
                    <tr
                      key={session._id}
                      className="border-b border-neutral-100 hover:bg-gradient-to-r hover:from-primary-50/50 hover:to-transparent transition-all duration-200"
                    >
                      <td className="py-2.5 px-3">
                        <p className="text-sm font-medium text-neutral-900">{session.name}</p>
                      </td>
                      <td className="py-2.5 px-3">
                        <p className="text-xs text-neutral-600">{session.organization?.name || 'N/A'}</p>
                      </td>
                      <td className="py-2.5 px-3">
                        <p className="text-xs text-neutral-600">{session.trainer ? `${session.trainer.firstName} ${session.trainer.lastName}` : 'N/A'}</p>
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <span className="text-xs font-medium text-neutral-700">{session.analytics?.totalParticipants || 0}</span>
                        </div>
                      </td>
                      <td className="py-2.5 px-3">
                        <Badge variant="completed" size="sm">
                          Completed
                        </Badge>
                      </td>
                      <td className="py-2.5 px-3 text-right">
                        <Button variant="ghost" size="sm" onClick={() => navigate(`/admin/sessions/${session._id}`)}>
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>

            {completedSessions.length === 0 && !loading && (
              <div className="text-center py-10">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-neutral-100 rounded-full mb-3">
                  <svg className="w-6 h-6 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                  </svg>
                </div>
                <p className="text-xs font-medium text-neutral-900">No completed sessions yet</p>
                <p className="text-xs text-neutral-500 mt-1">Sessions will appear here once they're completed</p>
              </div>
            )}
          </div>
        </div>

        {/* Platform Activity Feed - Timeline */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-base font-bold text-neutral-900">Platform Activity</h2>
              <p className="text-xs text-neutral-500">Recent events and updates</p>
            </div>
          </div>

          {platformActivity.length > 0 ? (
            <div className="relative">
              {platformActivity.map((activity, index) => {
                const isLast = index === platformActivity.length - 1;
                const iconPaths = {
                  organization: 'M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4',
                  user: 'M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z',
                  session: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
                  payment: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
                };
                return (
                  <div key={index} className="relative flex gap-4 pb-6 last:pb-0">
                    {/* Timeline line */}
                    {!isLast && (
                      <div className="absolute left-[17px] top-10 bottom-0 w-px bg-neutral-200" />
                    )}
                    {/* Icon */}
                    <div className={`relative z-10 w-9 h-9 rounded-full ${activity.color} flex items-center justify-center flex-shrink-0`}>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPaths[activity.type] || iconPaths.session} />
                      </svg>
                    </div>
                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-0.5">
                      <p className="text-sm text-neutral-900 font-medium">{activity.title}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-neutral-400">{activity.time}</span>
                        {activity.category && (
                          <>
                            <span className="text-neutral-300">·</span>
                            <span className="text-xs font-medium text-neutral-500">{activity.category}</span>
                          </>
                        )}
                      </div>
                      {activity.details && (
                        <div className="mt-2 px-3 py-2 bg-neutral-50 rounded-lg border border-neutral-100">
                          <p className="text-xs text-neutral-600 line-clamp-2">{activity.details}</p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-neutral-400 text-sm">No recent activity</div>
          )}

          <div className="mt-4 pt-4 border-t border-neutral-200">
            <Button variant="ghost" size="sm" fullWidth onClick={() => navigate('/admin/analytics')}>
              View All Activity
            </Button>
          </div>
        </div>

        {/* Quick Actions - Single Row */}
        <div className="bg-white rounded-xl shadow-sm border border-neutral-100 p-4">
          <div className="mb-3">
            <h2 className="text-base font-bold text-neutral-900">Quick Actions</h2>
            <p className="text-xs text-neutral-500">Common admin operations</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {[
                {
                  title: 'Add Organization', desc: 'Create new account',
                  onClick: () => navigate('/admin/organizations/new'),
                  variant: 'blue',
                  path: 'M12 6v6m0 0v6m0-6h6m-6 0H6',
                },
                {
                  title: 'Manage Users', desc: 'View all users',
                  onClick: () => navigate('/admin/users'),
                  variant: 'violet',
                  path: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
                },
                {
                  title: 'Analytics', desc: 'Platform insights',
                  onClick: () => navigate('/admin/analytics'),
                  variant: 'emerald',
                  path: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
                },
                {
                  title: 'Settings', desc: 'Platform config',
                  onClick: () => navigate('/admin/settings'),
                  variant: 'amber',
                  paths: [
                    'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
                    'M15 12a3 3 0 11-6 0 3 3 0 016 0z',
                  ],
                },
              ].map((qa) => (
                <button
                  key={qa.title}
                  type="button"
                  onClick={qa.onClick}
                  className="group rounded-2xl bg-white ring-1 ring-inset ring-neutral-200/70 p-4 text-left hover:-translate-y-0.5 hover:shadow-[0_4px_12px_-4px_rgba(16,24,40,0.10)] hover:ring-neutral-300 transition-all duration-200"
                >
                  <div className="flex items-center gap-3 sm:flex-col sm:items-start">
                    <span className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform ${STAT_CHIP[qa.variant]}`}>
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24">
                        {qa.paths
                          ? qa.paths.map((d, i) => <path key={i} strokeLinecap="round" strokeLinejoin="round" d={d} />)
                          : <path strokeLinecap="round" strokeLinejoin="round" d={qa.path} />}
                      </svg>
                    </span>
                    <div className="sm:mt-3">
                      <h3 className="font-semibold text-neutral-900 text-sm">{qa.title}</h3>
                      <p className="text-xs text-neutral-500 mt-0.5">{qa.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
      </div>
    </DashboardLayout>
  );
};

export default SuperAdminDashboard;
